import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useLanguage } from '../../../../../context/LanguageContext';
import '../style/faq-section.css';
import { initializeFAQSection } from '../script/faq-section';
import { initializeTitleAnimation } from '../script/title-animation';
import faqImage1 from '../../../../../assets/logo/faq/faq´s 358 x 594.webp';
import faqImage2 from '../../../../../assets/logo/faq/faq´s 766 x 165.webp';
import faqBgShape1 from '../../../../../assets/images/shapes/faq-one-bg-shape-1.png';
import faqBgShape2 from '../../../../../assets/images/shapes/faq-one-bg-shape-2.png';

const FAQSection: React.FC = () => {
    const { t, language } = useLanguage();
    const containerRef = useRef<HTMLElement>(null);
    const [activeAccordion, setActiveAccordion] = useState<number>(1); // Second accordion is active by default

    // Access translations - recalculate when language changes to ensure re-render
    const translation = useMemo(() => t as any, [t, language]);
    const faqTranslations = useMemo(() => translation.FAQ, [translation, language]);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const hasInitialized = sessionStorage.getItem('faq-titles-initialized');
        
        // Initialize title animation
        const titleTimer = setTimeout(() => {
            if (!hasInitialized) {
            initializeTitleAnimation();
                sessionStorage.setItem('faq-titles-initialized', 'true');
            } else {
                // On language change, just make titles visible without animation
                const titles = document.querySelectorAll('.faq-one .title-animation');
                titles.forEach((title: Element) => {
                    const el = title as HTMLElement;
                    el.style.opacity = '1';
                });
            }
            
            // Refresh ScrollTrigger
            if (typeof window !== 'undefined' && (window as any).ScrollTrigger) {
                (window as any).ScrollTrigger.refresh();
            }
        }, 100);

        // Initialize accordion functionality
        const timer = setTimeout(() => {
            initializeFAQSection(containerRef.current);
        }, 500);

        return () => {
            clearTimeout(timer);
            clearTimeout(titleTimer);
        };
    }, [language]); // Re-run when language changes

    const handleAccordionClick = (index: number, event: React.MouseEvent) => {
        event.preventDefault();
        const jQuery = (window as any).jQuery;
        
        if (typeof jQuery !== 'undefined' && containerRef.current) {
            const $accordion = jQuery(event.currentTarget).closest('.accrodion');
            const $group = $accordion.closest('.accrodion-grp');
            const $content = $accordion.find('.accrodion-content');
            
            if (!$accordion.hasClass('active')) {
                // Close all accordions in the group
                $group.find('.accrodion').removeClass('active');
                $group.find('.accrodion-content').slideUp();
                
                // Open clicked accordion
                $accordion.addClass('active');
                $content.slideDown();
                
                setActiveAccordion(index);
            }
        } else {
            // Fallback to React state if jQuery not available
            setActiveAccordion(activeAccordion === index ? -1 : index);
        }
    };

    return (
        <section className="faq-one" ref={containerRef}>
            <div className="faq-one__bg-shape" style={{ backgroundImage: `url(${faqBgShape1})` }}></div>
            <div className="faq-one__bg-shape" style={{ backgroundImage: `url(${faqBgShape2})` }}></div>
            <div className="container">
                <div className="row">
                    <div className="col-xl-6">
                        <div className="faq-one__left">
                            <div className="section-title text-left sec-title-animation animation-style2">
                                <div className="section-title__tagline-box">
                                    <div className="section-title__tagline-border">
                                        <div className="section-title__shape-1">
                                            <i className="section-title__circle"></i>
                                        </div>
                                    </div>
                                    <h6 className="section-title__tagline">
                                        {faqTranslations?.commonQuestions ?? 'Perguntas Frequentes'}
                                    </h6>
                                    <div className="section-title__tagline-border">
                                        <div className="section-title__shape-2">
                                            <i className="section-title__circle"></i>
                                        </div>
                                    </div>
                                </div>
                                <h3 
                                    className="section-title__title title-animation"
                                    key={`faq-title-${language}`}
                                    dangerouslySetInnerHTML={{ 
                                        __html: faqTranslations?.findAnswersToCommon ?? 'Encontre Respostas as<br class="mobile-only" /><span>Perguntas Mais Frequentes</span><br class="mobile-only" />dos Clientes'
                                    }}
                                ></h3>
                            </div>
                            <div className="accrodion-grp faq-one-accrodion" data-grp-name="faq-one-accrodion">
                                <div className={`accrodion ${activeAccordion === 0 ? 'active' : ''} wow fadeInLeft`} data-wow-delay="0ms" data-wow-duration="1500ms">
                                    <div className="accrodion-title" onClick={(e) => handleAccordionClick(0, e)}>
                                        <h4>{faqTranslations?.question1 ?? "O que está incluído numa revisão completa do carro?"}</h4>
                                    </div>
                                    <div className="accrodion-content" style={{ display: activeAccordion === 0 ? 'block' : 'none' }}>
                                        <div className="inner">
                                            <p>{faqTranslations?.answer1 ?? 'Uma revisão completa inclui mudança de óleo, substituição de filtros, inspeção de travões, verificação de pneus, teste de bateria e diagnóstico do motor.'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className={`accrodion ${activeAccordion === 1 ? 'active' : ''} wow fadeInRight`} data-wow-delay="100ms" data-wow-duration="1500ms">
                                    <div className="accrodion-title" onClick={(e) => handleAccordionClick(1, e)}>
                                        <h4>{faqTranslations?.question2 ?? 'Oferecem serviço de recolha e entrega?'}</h4>
                                    </div>
                                    <div className="accrodion-content" style={{ display: activeAccordion === 1 ? 'block' : 'none' }}>
                                        <div className="inner">
                                            <p>{faqTranslations?.answer2 ?? 'Recomendamos uma revisão completa a cada 6 meses ou 5.000–10.000 km, dependendo dos seus hábitos de condução.'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className={`accrodion ${activeAccordion === 2 ? 'active' : ''} wow fadeInLeft`} data-wow-delay="200ms" data-wow-duration="1500ms">
                                    <div className="accrodion-title" onClick={(e) => handleAccordionClick(2, e)}>
                                        <h4>{faqTranslations?.question3 ?? 'Quanto tempo demora uma revisão padrão?'}</h4>
                                    </div>
                                    <div className="accrodion-content" style={{ display: activeAccordion === 2 ? 'block' : 'none' }}>
                                        <div className="inner">
                                            <p>{faqTranslations?.answer3 ?? 'A maioria das revisões padrão são concluídas em 2–3 horas. Absolutamente! Desfrute da nossa sala de espera confortável com Wi-Fi e refrescos.'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className={`accrodion ${activeAccordion === 3 ? 'active' : ''} wow fadeInRight`} data-wow-delay="300ms" data-wow-duration="1500ms">
                                    <div className="accrodion-title" onClick={(e) => handleAccordionClick(3, e)}>
                                        <h4>{faqTranslations?.question4 ?? 'O vosso serviço tem garantia?'}</h4>
                                    </div>
                                    <div className="accrodion-content" style={{ display: activeAccordion === 3 ? 'block' : 'none' }}>
                                        <div className="inner">
                                            <p>{faqTranslations?.answer4 ?? 'Sim, oferecemos até 3 meses de garantia de serviço dependendo do tipo de reparação. Fornecemos estimativas antecipadas para que saiba exatamente o que esperar.'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className={`accrodion ${activeAccordion === 4 ? 'active' : ''} wow fadeInLeft`} data-wow-delay="400ms" data-wow-duration="1500ms">
                                    <div className="accrodion-title" onClick={(e) => handleAccordionClick(4, e)}>
                                        <h4>{faqTranslations?.question5 ?? 'Fornecem assistência de emergência na estrada?'}</h4>
                                    </div>
                                    <div className="accrodion-content" style={{ display: activeAccordion === 4 ? 'block' : 'none' }}>
                                        <div className="inner">
                                            <p>{faqTranslations?.answer5 ?? 'Sim, está disponível apoio de reboque de emergência 24/7 e reparação na estrada. Fornecemos estimativas antecipadas para que saiba exatamente o que esperar.'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-6">
                        <div className="faq-one__right">
                            <div className="faq-one__img-box">
                                <div className="faq-one__img wow fadeInDown" data-wow-delay="100ms">
                                    <img loading="lazy" src={faqImage1} alt="" className="float-bob-y" />
                                </div>
                                <div className="faq-one__img-2">
                                    <img loading="lazy" src={faqImage2} alt="" style={{ width: '130%', maxWidth: '900px', height: 'auto', paddingLeft: '15px' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FAQSection;

